import axios from 'axios';

/**
 * CORS Handler Utility for Managing Cross-Origin Requests
 * Provides fallback strategies and enhanced error handling for CORS issues
 */
class CorsHandler {
  constructor() {
    this.corsAttempts = new Map(); // Track CORS attempt history
    this.fallbackStrategies = [];
  }

  /**
   * Create axios instance with CORS-optimized configuration
   */
  createCorsOptimizedAxios(baseURL, options = {}) {
    const config = {
      baseURL,
      timeout: 30000,
      withCredentials: false, // Essential for CORS to different domains
      
      // CORS-friendly headers
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Cache-Control': 'no-cache',
        // Remove potentially problematic headers for CORS
        'X-Requested-With': 'XMLHttpRequest',
      },
      
      // Additional CORS configuration
      maxRedirects: 5,
      validateStatus: (status) => status < 500, // Don't reject on 4xx errors
      
      ...options
    };

    const instance = axios.create(config);

    // Add CORS-specific request interceptor
    instance.interceptors.request.use(
      (config) => {
        // Track CORS attempts for this endpoint
        const key = `${config.method?.toUpperCase()}_${config.url}`;
        this.corsAttempts.set(key, {
          timestamp: Date.now(),
          attempts: (this.corsAttempts.get(key)?.attempts || 0) + 1
        });

        // Log CORS attempt
        console.log(`🌐 CORS Request: ${config.method?.toUpperCase()} ${config.url}`);
        
        return config;
      },
      (error) => {
        console.error('❌ CORS Request Interceptor Error:', error);
        return Promise.reject(error);
      }
    );

    // Add CORS-specific response interceptor
    instance.interceptors.response.use(
      (response) => {
        console.log(`✅ CORS Success: ${response.status} ${response.config.url}`);
        return response;
      },
      (error) => {
        return this.handleCorsError(error);
      }
    );

    return instance;
  }

  /**
   * Enhanced CORS error handling with detailed diagnostics
   */
  async handleCorsError(error) {
    const { config, code, message, response } = error;
    
    // Detect CORS-related errors
    const isCorsError = 
      code === 'ERR_NETWORK' ||
      message.includes('CORS') ||
      message.includes('Access-Control-Allow-Origin') ||
      message.includes('preflight') ||
      (response?.status === 0 && !response?.statusText);

    if (isCorsError) {
      console.error('🚫 CORS Error Detected:', {
        url: config?.url,
        method: config?.method,
        code,
        message
      });

      // Provide detailed CORS error information
      const corsErrorDetails = {
        success: false,
        corsError: true,
        error: 'CORS Policy Violation',
        details: {
          message: 'The API server is not configured to accept requests from this domain.',
          technicalDetails: message,
          possibleCauses: [
            'API server missing Access-Control-Allow-Origin header',
            'API server not configured for preflight requests',
            'Incorrect request headers causing preflight failure',
            'Network connectivity issues'
          ],
          suggestedSolutions: [
            'Contact API administrator to configure CORS headers',
            'Verify API endpoint is accessible',
            'Check network connectivity',
            'Try alternative request method if available'
          ]
        },
        requestInfo: {
          url: config?.url,
          method: config?.method?.toUpperCase(),
          headers: Object.keys(config?.headers || {}),
          timestamp: new Date().toISOString()
        }
      };

      return Promise.resolve(corsErrorDetails);
    }

    // Handle non-CORS errors normally
    return Promise.reject(error);
  }

  /**
   * Test CORS connectivity to an endpoint
   */
  async testCorsConnectivity(baseURL, endpoint = '/') {
    try {
      const testAxios = this.createCorsOptimizedAxios(baseURL);
      
      console.log(`🔍 Testing CORS connectivity to: ${baseURL}${endpoint}`);
      
      // Try a simple GET request first
      const response = await testAxios.get(endpoint);
      
      return {
        success: true,
        status: response.status,
        message: 'CORS connectivity test passed',
        headers: response.headers
      };
      
    } catch (error) {
      console.error('🔍 CORS connectivity test failed:', error.message);
      
      return {
        success: false,
        error: error.message,
        corsSupported: false,
        recommendation: 'API server needs CORS configuration'
      };
    }
  }

  /**
   * Get CORS diagnostics for troubleshooting
   */
  getCorsdiagnostics() {
    return {
      attemptHistory: Array.from(this.corsAttempts.entries()).map(([key, data]) => ({
        endpoint: key,
        ...data
      })),
      currentOrigin: window.location.origin,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Clear CORS attempt history
   */
  clearCorsHistory() {
    this.corsAttempts.clear();
    console.log('🧹 CORS attempt history cleared');
  }
}

// Export singleton instance
export const corsHandler = new CorsHandler();
export default corsHandler; 